<script setup lang="ts">
import { ref, reactive, computed, nextTick } from 'vue'
import axios from 'axios'
import LocationGeneral from './LocationGeneral.vue'
import LocationAppData from './LocationAppData.vue'
import LocationWefo from './LocationWefo.vue'

interface LocationData {
  id?: number
  title: string
  slug: string
  street: string
  street_number: string
  zip: string
  city: string
  areal: string
  locationgroup: string
  latitude: string
  longitude: string
  email: string
  telephone: string
  public: boolean
  icon: string
  properties: object
  syncs_with_data_hub: boolean
  wefo_enabled: boolean
  wefo_data: any
}

interface TagOption {
  id: number
  title: string
}

interface IconOptionItem {
  label: string
  name: string
  svg?: string
}

interface IconOption {
  id: number
  name: string
  image_url: string | null
}

interface SelectedIcon {
  id: number
  name: string
  image_url: string | null
  description: string
  sort_order: number
}

const props = defineProps<{
  mode: 'create' | 'edit'
  location?: LocationData
  tags: TagOption[]
  selectedTagIds: number[]
  iconOptions: IconOptionItem[]
  allIcons: IconOption[]
  selectedIcons: SelectedIcon[]
  actionUrl: string
}>()

// Tab state
const activeTab = ref(0)
const tabs = ['Allgemein', 'App / Data Hub', 'Welterbe-Forum']

// Form data
const form = reactive({
  title: props.location?.title ?? '',
  slug: props.location?.slug ?? '',
  street: props.location?.street ?? '',
  street_number: props.location?.street_number ?? '',
  zip: props.location?.zip ?? '',
  city: props.location?.city ?? '',
  areal: props.location?.areal ?? '',
  locationgroup: props.location?.locationgroup ?? '',
  latitude: props.location?.latitude ?? '',
  longitude: props.location?.longitude ?? '',
  email: props.location?.email ?? '',
  telephone: props.location?.telephone ?? '',
  public: props.location?.public ?? false,
})

const properties = ref<object>(props.location?.properties ?? {})
const selectedTagIds = ref<number[]>([...props.selectedTagIds])
const selectedIcon = ref<string>(props.location?.icon ?? '')
const syncsWithDataHub = ref<boolean>(props.location?.syncs_with_data_hub ?? false)
const wefoEnabled = ref<boolean>(props.location?.wefo_enabled ?? false)
const wefoData = ref<any>(props.location?.wefo_data ?? null)
const wefoIcons = ref<SelectedIcon[]>([...props.selectedIcons])

// UI state
const saving = ref(false)
const successMessage = ref('')
const serverErrors = ref<Record<string, string[]>>({})

// Map fields to tabs for error highlighting
const tabFieldMap: Record<number, string[]> = {
  0: ['title', 'slug', 'street', 'street_number', 'zip', 'city', 'areal', 'locationgroup', 'latitude', 'longitude', 'email', 'telephone', 'public'],
  1: ['tags', 'icon', 'properties'],
  2: ['wefo_enabled', 'wefo_data', 'wefo_icons'],
}

const tabHasError = (tabIndex: number): boolean => {
  const fields = tabFieldMap[tabIndex]
  return fields.some(field => {
    // Check direct match
    if (serverErrors.value[field]) return true
    // Check prefix match (e.g. wefo_data.walking_time, properties.openingHoursStructured...)
    return Object.keys(serverErrors.value).some(key => key.startsWith(field + '.'))
  })
}

// Reference to general tab for vuelidate
const generalRef = ref<InstanceType<typeof LocationGeneral> | null>(null)
const wefoRef = ref<InstanceType<typeof LocationWefo> | null>(null)

// Human-readable field labels for the error alert
const fieldLabels: Record<string, string> = {
  title: 'Titel',
  street: 'Straße',
  street_number: 'Hausnummer',
  zip: 'PLZ',
  city: 'Stadt',
  email: 'E-Mail',
  telephone: 'Telefon',
  locationgroup: 'Gruppe',
  areal: 'Areal',
  latitude: 'Latitude',
  longitude: 'Longitude',
  tile_title: 'Kachel-Titel',
  title1: 'Titel 1',
  title2: 'Titel 2',
  description: 'Beschreibung',
  location_text: 'Standort-Text',
  walking_time: 'Fußweg (Minuten)',
  prices: 'Preise',
  index_titles: 'Index-Titel',
}

interface ClientError {
  label: string
  message: string
  tab: string
}

const fieldToTab: Record<string, string> = {
  title: 'Allgemein',
  street: 'Allgemein',
  street_number: 'Allgemein',
  zip: 'Allgemein',
  city: 'Allgemein',
  email: 'Allgemein',
  telephone: 'Allgemein',
  locationgroup: 'Allgemein',
  areal: 'Allgemein',
  latitude: 'Allgemein',
  longitude: 'Allgemein',
  tile_title: 'Welterbe-Forum',
  title1: 'Welterbe-Forum',
  title2: 'Welterbe-Forum',
  description: 'Welterbe-Forum',
  location_text: 'Welterbe-Forum',
  walking_time: 'Welterbe-Forum',
  prices: 'Welterbe-Forum',
  index_titles: 'Welterbe-Forum',
}

const groupedClientErrors = computed(() => {
  const groups: Record<string, ClientError[]> = {}
  for (const err of clientErrors.value) {
    const tab = err.tab || 'Sonstige'
    if (!groups[tab]) groups[tab] = []
    groups[tab].push(err)
  }
  return groups
})

// Client-side validation errors (aggregated for alert)
const clientErrors = ref<ClientError[]>([])

const tabHasClientError = (tabIndex: number): boolean => {
  if (tabIndex === 0 && generalRef.value?.v$) {
    return generalRef.value.v$.$errors.length > 0
  }
  if (tabIndex === 2 && wefoRef.value?.v$) {
    return wefoRef.value.v$.$errors.length > 0
  }
  return false
}

const tabHasAnyError = (tabIndex: number): boolean => {
  return tabHasError(tabIndex) || tabHasClientError(tabIndex)
}

const submit = async () => {
  // Reset
  serverErrors.value = {}
  successMessage.value = ''
  clientErrors.value = []

  let hasValidationError = false

  // Frontend validation - General tab
  if (generalRef.value?.v$) {
    const valid = await generalRef.value.v$.$validate()
    if (!valid) {
      hasValidationError = true
    }
  }

  // Frontend validation - WeFo tab (only if enabled)
  if (wefoEnabled.value && wefoRef.value?.v$) {
    const valid = await wefoRef.value.v$.$validate()
    if (!valid) {
      hasValidationError = true
    }
  }

  if (hasValidationError) {
    // Collect all client errors with field labels for the alert
    const toClientErrors = (v$Instance: any): ClientError[] =>
      v$Instance.$errors.map((e: any) => ({
        label: fieldLabels[e.$property] ?? e.$property,
        message: e.$message,
        tab: fieldToTab[e.$property] ?? 'Sonstige',
      }))

    const errors: ClientError[] = [
      ...(generalRef.value?.v$ ? toClientErrors(generalRef.value.v$) : []),
      ...(wefoRef.value?.v$ ? toClientErrors(wefoRef.value.v$) : []),
    ]
    clientErrors.value = errors

    // Switch to first tab with error
    for (let i = 0; i < tabs.length; i++) {
      if (tabHasClientError(i)) {
        activeTab.value = i
        break
      }
    }
    return
  }

  saving.value = true

  const payload: Record<string, any> = {
    ...form,
    properties: JSON.stringify(properties.value),
    tags: JSON.stringify(selectedTagIds.value),
    icon: selectedIcon.value,
    syncs_with_data_hub: syncsWithDataHub.value ? '1' : '',
    wefo_enabled: wefoEnabled.value ? '1' : '0',
    wefo_data: wefoEnabled.value ? JSON.stringify(wefoData.value) : null,
    wefo_icons: JSON.stringify(wefoIcons.value.map(icon => ({
      id: icon.id,
      description: icon.description,
      sort_order: icon.sort_order,
    }))),
    public: form.public ? '1' : '',
  }

  try {
    const response = await axios.post(props.actionUrl, payload, {
      headers: { 'Accept': 'application/json' }
    })

    if (props.mode === 'create' && response.data.redirect_url) {
      window.location.href = response.data.redirect_url
    } else {
      successMessage.value = 'Änderungen gespeichert.'
      setTimeout(() => { successMessage.value = '' }, 3000)
    }
  } catch (error: any) {
    if (error.response?.status === 422 && error.response.data?.errors) {
      clientErrors.value = []
      serverErrors.value = error.response.data.errors
      // Switch to first tab with error
      await nextTick()
      for (let i = 0; i < tabs.length; i++) {
        if (tabHasError(i)) {
          activeTab.value = i
          break
        }
      }
    } else {
      serverErrors.value = { _general: ['Ein unerwarteter Fehler ist aufgetreten.'] }
    }
  } finally {
    saving.value = false
  }
}

// Computed errors filtered for WeFo tab
const wefoErrors = computed(() => {
  const filtered: Record<string, string[]> = {}
  for (const [key, val] of Object.entries(serverErrors.value)) {
    if (key.startsWith('wefo_data') || key.startsWith('wefo_icons')) {
      filtered[key] = val
    }
  }
  return filtered
})
</script>

<template>
  <div>
    <!-- Success message -->
    <div v-if="successMessage" role="alert" class="alert alert-success mb-4">
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      <span>{{ successMessage }}</span>
    </div>

    <!-- General error (non-422) -->
    <div v-if="serverErrors._general" role="alert" class="alert alert-error mb-4">
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      <span>{{ serverErrors._general[0] }}</span>
    </div>

    <!-- Client validation errors -->
    <div v-if="clientErrors.length > 0" role="alert" class="alert alert-error mb-4 items-start">
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6 mt-0.5" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
      <div class="w-full">
        <p class="font-semibold mb-1">Bitte korrigieren Sie folgende Fehler:</p>
        <div v-for="(errors, tabName) in groupedClientErrors" :key="tabName" class="mt-1">
          <p class="font-bold opacity-80">{{ tabName }}</p>
          <ul class="list-disc ml-4">
            <li v-for="(err, i) in errors" :key="i"><strong>{{ err.label }}:</strong> {{ err.message }}</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Validation errors summary -->
    <div v-if="Object.keys(serverErrors).filter(k => k !== '_general').length > 0" role="alert" class="alert alert-error mb-4">
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      <div>
        <p class="font-semibold">Bitte prüfen Sie die markierten Tabs.</p>
        <ul class="list-disc ml-4 mt-1">
          <template v-for="(messages, field) in serverErrors" :key="field">
            <template v-if="field !== '_general'">
              <li v-for="msg in messages" :key="msg">{{ msg }}</li>
            </template>
          </template>
        </ul>
      </div>
    </div>

    <!-- Tabs -->
    <div role="tablist" class="tabs tabs-bordered grid-cols-3 mb-6">
      <button
        v-for="(tab, index) in tabs"
        :key="index"
        role="tab"
        type="button"
        class="tab relative"
        :class="{ 'tab-active': activeTab === index, 'tab-error': tabHasAnyError(index) }"
        @click="activeTab = index"
      >
        {{ tabs[index] }}
        <span v-if="tabHasAnyError(index)" class="ml-2 w-1.5 h-1.5 bg-error rounded-full"></span>
      </button>
    </div>

    <!-- Tab Panels -->
    <div v-show="activeTab === 0">
      <location-general
        ref="generalRef"
        v-model="form"
        :errors="serverErrors"
        :mode="mode"
      />
    </div>

    <div v-show="activeTab === 1">
      <location-app-data
        :properties="properties"
        :tags="tags"
        :selected-tag-ids="selectedTagIds"
        :icon-options="iconOptions"
        :selected-icon="selectedIcon"
        :syncs-with-data-hub="syncsWithDataHub"
        :errors="serverErrors"
        :mode="mode"
        @update:properties="(val) => properties = val"
        @update:selected-tag-ids="(val) => selectedTagIds = val"
        @update:selected-icon="(val) => selectedIcon = val"
        @update:syncs-with-data-hub="(val) => syncsWithDataHub = val"
      />
    </div>

    <div v-show="activeTab === 2">
      <location-wefo
        ref="wefoRef"
        :wefo-enabled="wefoEnabled"
        :wefo-data="wefoData"
        :all-icons="allIcons"
        :selected-icons="selectedIcons"
        :location-id="location?.id ?? null"
        :errors="wefoErrors"
        @update:wefo-enabled="(val) => wefoEnabled = val"
        @update:wefo-data="(val) => wefoData = val"
        @update:wefo-icons="(val) => wefoIcons = val"
      />
    </div>

    <!-- Actions row -->
    <div class="flex items-center gap-3 mt-6">
      <button type="button" class="btn btn-success text-white" :disabled="saving" @click="submit">
        <span v-if="saving" class="loading loading-spinner loading-sm"></span>
        {{ mode === 'create' ? 'Erstellen' : 'Speichern' }}
      </button>
      <slot name="actions"></slot>
    </div>

    <!-- Feedback below buttons -->
    <div v-if="successMessage" role="alert" class="alert alert-success mt-6">
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      <span>{{ successMessage }}</span>
    </div>
    <div v-if="serverErrors._general" role="alert" class="alert alert-error mt-6">
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      <span>{{ serverErrors._general[0] }}</span>
    </div>

    <div v-if="mode === 'create'" class="mt-2">
      <small class="italic">Hinweis: Bilder können nach dem Erstellen hochgeladen werden.</small>
    </div>
  </div>
</template>

<style scoped>
.tab-error {
  --tab-color: oklch(var(--er)) !important;
  color: oklch(var(--er)) !important;
  font-weight: 600;
}
.tab-error.tab-active {
  --tab-border-color: oklch(var(--er)) !important;
  border-bottom-color: oklch(var(--er)) !important;
}
</style>
